// Every error and warning across the whole roster, grouped by file, each
// one a click from its line - the Problems view every IDE and every
// reference game editor (Godot's status-bar error list, Roblox's Script
// Analysis window) puts beside the code. Fed by scriptProblemsStore, which
// checks against the same real `check()` and the same project grant as the
// inline squiggles, so this list and the editor never disagree.
import { useMemo } from 'react';

import { revealInScript } from '../scripting/editorCommands';
import { countProblems, useScriptProblemsStore, type ScriptProblem } from '../scripting/scriptProblemsStore';
import { useScriptStore } from '../../scripting/scriptStore';
import { CodeIcon } from './codeIcons';
import { CwSectionLabel, ScriptFileName } from './codeShared';

function ProblemRow({ problem }: { problem: ScriptProblem }) {
  const go = () => revealInScript(problem.scriptId, problem.startLineNumber, problem.startColumn);
  return (
    <div className={`cw-row cw-problem cw-problem--${problem.severity}`} onClick={go} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && go()}>
      <span className="cw-row-icon">
        <CodeIcon kind={problem.severity === 'error' ? 'error' : 'warning'} size={14} />
      </span>
      <span className="cw-problem-text">{problem.message}</span>
      <span className="cw-problem-loc">
        {problem.startLineNumber}:{problem.startColumn}
      </span>
    </div>
  );
}

export function CodeProblemsPanel() {
  const problems = useScriptProblemsStore((s) => s.problems);
  const scripts = useScriptStore((s) => s.scripts);
  const rosterLoaded = useScriptStore((s) => s.rosterLoaded);

  const groups = useMemo(() => {
    const byScript = new Map<string, ScriptProblem[]>();
    for (const problem of problems) {
      const list = byScript.get(problem.scriptId) ?? [];
      list.push(problem);
      byScript.set(problem.scriptId, list);
    }
    // Roster order (alphabetical, like the Scripts list), errors before
    // warnings within a file, then by position.
    return [...scripts]
      .sort((a, b) => a.name.localeCompare(b.name))
      .filter((s) => byScript.has(s.id))
      .map((s) => ({
        script: s,
        problems: byScript.get(s.id)!.slice().sort((a, b) => (a.severity === b.severity ? a.startLineNumber - b.startLineNumber : a.severity === 'error' ? -1 : 1)),
      }));
  }, [problems, scripts]);

  const totals = countProblems(problems);

  return (
    <div className="cw-fill cw-fill--side">
      <CwSectionLabel
        action={
          totals.errors + totals.warnings > 0 ? (
            <span style={{ display: 'flex', gap: 5 }}>
              {totals.errors > 0 && <span className="cw-pill cw-pill--error">{totals.errors} {totals.errors === 1 ? 'error' : 'errors'}</span>}
              {totals.warnings > 0 && <span className="cw-pill cw-pill--warning">{totals.warnings} {totals.warnings === 1 ? 'warning' : 'warnings'}</span>}
            </span>
          ) : undefined
        }
      >
        Problems
      </CwSectionLabel>
      <div className="cw-list cw-list--scroll" style={{ flex: 1, paddingBottom: 12 }} role="list">
        {rosterLoaded && problems.length === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '32px 20px', textAlign: 'center' }}>
            <span style={{ color: 'var(--ss-success)' }}>
              <CodeIcon kind="check" size={20} />
            </span>
            <span style={{ fontSize: 'var(--ss-fs-sm)', fontWeight: 500, color: 'var(--ss-text-2)' }}>No problems</span>
            <span style={{ fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)', lineHeight: 1.6 }}>
              {scripts.length === 0 ? 'Create a script and this list will check it as you type.' : 'Every script checks clean against the capabilities this project grants.'}
            </span>
          </div>
        )}
        {groups.map(({ script, problems: list }) => {
          const counts = countProblems(list);
          return (
            <div key={script.id}>
              <div className="cw-problem-file">
                <CodeIcon kind="file" size={13} />
                <ScriptFileName name={script.name} />
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                  {counts.errors > 0 && <span className="cw-pill cw-pill--error">{counts.errors}</span>}
                  {counts.warnings > 0 && <span className="cw-pill cw-pill--warning">{counts.warnings}</span>}
                </span>
              </div>
              {list.map((problem, i) => <ProblemRow key={`${problem.startLineNumber}:${problem.startColumn}:${i}`} problem={problem} />)}
            </div>
          );
        })}
      </div>
    </div>
  );
}
